/**
 * AddItemModal - Modal para adicionar item manualmente na contagem
 *
 * Funcionalidades:
 * - Busca de produto por código ou descrição (autocomplete)
 * - Data de validade no formato DD/MM/AAAA
 * - Seleção de cliente e regime tributário
 */
import React, { useEffect } from 'react';
import { X, Plus, AlertCircle, Search, Loader2 } from 'lucide-react';
import type { Cliente } from '../../services/clienteService';
import { useAddItemForm, type AddItemFormData } from '../../hooks/useAddItemForm';

export type { AddItemFormData };

interface AddItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AddItemFormData) => Promise<void>;
  jobClientes: Cliente[];
  loading: boolean;
}

export const AddItemModal: React.FC<AddItemModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  jobClientes,
  loading,
}) => {
  const {
    formData,
    setFormData,
    error,
    setError,
    showRegimeSelector,
    dateInput,
    searchTerm,
    searchResults,
    isSearching,
    showDropdown,
    setShowDropdown,
    selectedProduct,
    dropdownRef,
    handleSearchChange,
    handleSelectProduct,
    handleClienteChange,
    handleDateChange,
    validate,
  } = useAddItemForm({ isOpen, jobClientes });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef, setShowDropdown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      await onSubmit(formData);
    } catch (err: any) {
      setError(err.message || 'Erro ao adicionar item');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-4 flex items-center justify-between rounded-t-xl sticky top-0">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Plus size={24} />
            Adicionar Item Manual
          </h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Cliente Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Cliente *
            </label>
            <select
              value={formData.cliente_id}
              onChange={(e) => handleClienteChange(Number(e.target.value))}
              disabled={loading || jobClientes.length === 1}
              className="w-full px-4 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
            >
              {jobClientes.length > 1 && <option value={0}>Selecione um cliente...</option>}
              {jobClientes.map((cliente) => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.nome_fantasia || cliente.razao_social}
                </option>
              ))}
            </select>
          </div>

          {/* Regime Selection (conditional) */}
          {showRegimeSelector && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Regime Especial *
              </label>
              <select
                value={formData.com_regime === null ? '' : (formData.com_regime ? 'true' : 'false')}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData(prev => ({ ...prev, com_regime: val === '' ? null : val === 'true' }));
                }}
                disabled={loading}
                className="w-full px-4 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Selecione o regime...</option>
                <option value="true">Com Regime</option>
                <option value="false">Sem Regime</option>
              </select>
            </div>
          )}

          {/* Product Search with Autocomplete */}
          <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Produto * <span className="font-normal text-gray-500">(digite código ou descrição)</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                disabled={loading || formData.cliente_id === 0}
                placeholder={formData.cliente_id === 0 ? "Selecione um cliente primeiro" : "Digite para buscar..."}
                className="w-full px-4 py-2 pl-10 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                {isSearching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              </div>
            </div>

            {/* Dropdown Results */}
            {showDropdown && searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchResults.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => handleSelectProduct(product)}
                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border-b border-gray-100 dark:border-gray-600 last:border-b-0 transition"
                  >
                    <div className="text-gray-800 dark:text-gray-200">
                      <span className="font-semibold text-emerald-700 dark:text-emerald-400">{product.codigo}</span>
                      <span className="mx-1">-</span>
                      <span>{product.descricao_completa}</span>
                    </div>
                    {product.fabricante && (
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        Fabricante: {product.fabricante}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* No results message */}
            {showDropdown && searchResults.length === 0 && searchTerm.length >= 2 && !isSearching && (
              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-4 text-center text-gray-500">
                Nenhum produto encontrado
              </div>
            )}
          </div>

          {/* Selected Product Info */}
          {selectedProduct && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg p-3">
              <div className="text-sm">
                <span className="font-semibold text-emerald-700 dark:text-emerald-400">Produto selecionado:</span>
                <span className="ml-2 text-gray-700 dark:text-gray-300">
                  {selectedProduct.codigo} - {selectedProduct.descricao_completa}
                </span>
              </div>
            </div>
          )}

          {/* Lote and Validade Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Lote *
              </label>
              <input
                type="text"
                value={formData.lote}
                onChange={(e) => setFormData(prev => ({ ...prev, lote: e.target.value }))}
                disabled={loading}
                placeholder="Ex: T2J103X"
                className="w-full px-4 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Data de Validade * <span className="font-normal text-gray-500">(DD/MM/AAAA)</span>
              </label>
              <input
                type="text"
                value={dateInput}
                onChange={(e) => handleDateChange(e.target.value)}
                disabled={loading}
                placeholder="DD/MM/AAAA"
                maxLength={10}
                className="w-full px-4 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Quantidade, Contada e Coletar Row */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Quantidade (Sistema) *
              </label>
              <input
                type="number"
                value={formData.quantidade}
                onChange={(e) => setFormData(prev => ({ ...prev, quantidade: Number(e.target.value) }))}
                disabled={loading}
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Qtde. Contada <span className="font-normal text-gray-500">(opcional)</span>
              </label>
              <input
                type="number"
                value={formData.quantidade_contada === null ? '' : formData.quantidade_contada}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  quantidade_contada: e.target.value === '' ? null : Number(e.target.value),
                }))}
                disabled={loading}
                min="0"
                step="0.01"
                placeholder="—"
                className="w-full px-4 py-2 border-2 border-emerald-200 dark:border-emerald-600 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Coletar <span className="font-normal text-gray-500">(opcional)</span>
              </label>
              <input
                type="number"
                value={formData.quantidade_coletar === 0 ? '' : formData.quantidade_coletar}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  quantidade_coletar: e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0,
                }))}
                disabled={loading}
                min="0"
                step="1"
                placeholder="0"
                className="w-full px-4 py-2 border-2 border-amber-200 dark:border-amber-600 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-gray-900 dark:text-white focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded flex items-start gap-3">
              <AlertCircle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition font-medium disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-lg hover:from-emerald-600 hover:to-green-600 transition font-medium disabled:opacity-50"
            >
              <Plus size={18} />
              {loading ? 'Adicionando...' : 'Adicionar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
